<template>
  <div
    style="width: 100%"
    :class="{ 'd-flex align-center': withExportButton }"
  >
    <RegisteredField
      v-if="isFormElement"
      v-slot="{ errors }"
      :vid="vid"
      :name="label"
      :rules="rules"
      class="flex-grow-1"
    >
      <keep-alive>
        <component
          :is="dynamicComponent"
          v-bind="contactPickerProps"
          :path="path"
          :errors="errors"
          :mandatory-filters="computedMandatoryFilter"
          :multiple="multipleCompanyFilter"
          v-on="$listeners"      
          @click:openDialog="handleOpenDialog"
        >
          <template #tooltip>
            <slot name="tooltip" />
          </template>
        </component>
      </keep-alive>
    </RegisteredField>

    <keep-alive v-else>
      <component
        :is="dynamicComponent"
        v-bind="contactPickerProps"
        :path="path"
        :errors="errors"
        class="flex-grow-1"
        :mandatory-filters="computedMandatoryFilter"
        v-on="$listeners"
        @click:openDialog="handleOpenDialog"
      >
        <template #tooltip>
          <slot name="tooltip" />
        </template>
      </component>
    </keep-alive>

    <w-export-button
      v-if="withExportButton && hasExportId"
      @click="navigateToEntry"
    >
      <template #tooltip>
        {{ $t('Components.sentences.navigateToEntry', [label]) }}
      </template>
    </w-export-button>

    <ContactPickerDialog
      ref="contactDialog"
      v-bind="contactDialogProps"
      :path="path"
      :resultPageSize="resultPageSize"
      :filter-preset="filterPreset"
      :show-inquiry-data="showInquiryData"
      :mandatory-filters="computedMandatoryFilter"
      :dialogTitle="dialogTitle"
      :show-is-employee-filter="showIsEmployeeFilter"
      :multiple-company-filter="multipleCompanyFilter"
    >
      <template #header>
        <slot name="header" />
      </template>

      <template #filters>
        <slot name="filters" />
      </template>
      
      <template #left-prepend-content>
        <slot name="left-prepend-content-value" />
      </template>
    </contactpickerdialog>
  </div>
</template>

<script>
// components
import WorkContactPickerWithInputField from '@/components/global_components/pickers/contact_picker/components/WorkContactPickerWithInputField';
import WorkContactPickerOnlyInputField from '@/components/global_components/pickers/contact_picker/components/WorkContactPickerOnlyInputField';
import WorkDefaultContactPicker from '@/components/global_components/pickers/contact_picker/components/WorkDefaultContactPicker';

// mixins
import contactPickerProps from '@/components/global_components/pickers/contact_picker/mixins/contact-picker-props';
import contactPickerDialogProps from '@/components/global_components/pickers/contact_picker/mixins/contact-picker-dialog-props';

// services & utils
import contactService from '@/modules/contacts/services/contact-service';
import { filterProps } from '@/utils/functional-component-utils';
import i18n from '@/language/i18n';
import get from 'lodash/get';

// store
import { mapActions } from 'vuex';
import RegisteredField from '@/validation/RegisteredField';

const isInApiObjectFormat = (item) =>
  item.id != undefined && item.data != undefined;
const DEFAULT_ERROR = (label) => ({
  fieldName: label,
  message: i18n.t('Components.sentences.personNotFound'),
});
const NECESSARY_PROPS = ['path'];

export default {
  name: 'WorkContactPicker',
  components: {
    RegisteredField,
    ContactPickerDialog: () =>
      import(
        '@/components/global_components/pickers/contact_picker/dialogs/ContactPickerDialog'
      ),
  },
  props: {
    showInquiryData: { type: Boolean, default: false }, 
    multipleCompanyFilter: { type: Boolean, default: false },
  },
  mixins: [contactPickerProps, contactPickerDialogProps],
  computed: {
    computedMandatoryFilter() {
      return {
        ...this.mandatoryFilters
      }
    },
    dynamicComponent() {
      if (this.withInputField) {
        return WorkContactPickerWithInputField;
      }
      if (this.onlyInputField) {
        return WorkContactPickerOnlyInputField;
      }
      return WorkDefaultContactPicker;
    },
    contactPickerProps() {
      return filterProps(
        this.$props,
        this._getFilterableProps(contactPickerDialogProps)
      );
    },
    contactDialogProps() {
      return filterProps(
        this.$props,
        this._getFilterableProps(contactPickerProps)
      );
    },
    hasExportId() {
      if (this.withExportButton) {
        return get(this.value, this.exportValue) != undefined;
      }
      return false;
    },
  },
  methods: {
    ...mapActions('routingHistory', [
      'redirectToLastHistoryObject',
      'setIntentionalUnusualForwarding',
    ]),
    ...mapActions('errorNotificationStore', [
      'setErrors',
      'showErrorNotification',
    ]),
    async handleOpenDialog() {
      let contact = await this.$refs.contactDialog.open();

      if (this.noDataReload) {
        return this._handleNewContactWithoutReload(contact);
      }
      return this._handleNewContactWithReload(contact);
    },
    _handleNewContactWithoutReload(contact) {
      if (this.multiple) {
        let formattedMultipleData = contact.map(item => ({ id: ''+item.id, data: { ...item }}));
        this.$emit('input', formattedMultipleData);
      }
      else this.$emit('input', this._formatToNecessaryFormat(contact));
    },
    async _handleNewContactWithReload(contact) {
      let loadedContact = await this._loadContact(contact.id);
      this.$emit('input', loadedContact);
    },
    navigateToEntry() {
      this.setIntentionalUnusualForwarding(true);
      this.$router.push({
        name: this.exportComponent,
        params: {
          id: get(this.value, this.exportValue),
          redirectAfterSave: this.redirectToLastHistoryObject,
        },
      });
    },
    /******************************
     * ### RELOAD FUNCTIONALITY ###
     *****************************/
    async _loadContact(contactId) {
      try {
        let contactResponse = await contactService.loadData(contactId);
        return contactResponse.data;
      } catch (e) {
        this._handleError();
      }
    },
    _handleError() {
      this.setErrors([DEFAULT_ERROR(this.label)]);
      this.showErrorNotification();
    },
    /**************************
     * ### INTERNAL HELPERS ###
     *************************/
    _formatToNecessaryFormat(contact) {
      
      if (this.returnObject && !isInApiObjectFormat(contact)) {
        return { id: String(contact.id), data: { ...contact } };
      }
      return String(contact.id);
    },
    _getFilterableProps(mixin) {
      let byNecessaryProps = (propKey) => !NECESSARY_PROPS.includes(propKey);
      return Object.keys(mixin.props).filter(byNecessaryProps);
    },
  },
};
</script>
